import type { ThemePreference } from "@healthos/analytics";
import { useSyncExternalStore } from "react";

/**
 * Colour theme. A module store like `sound.ts`: the preference, its
 * persistence and the `<html data-theme>` attribute live here, so every
 * reader gets the same truth through `useTheme()` with no provider.
 *
 * The tokens own what a theme looks like (`mode.dark` in tokens.json emits
 * `:root[data-theme="dark"]`); this module only decides which one is on.
 * index.html runs the same resolution inline before first paint, so a dark
 * member never sees a light frame. Keep STORAGE_KEY and the attribute in step
 * with that script.
 *
 * Dark sits behind the member-scoped `enableDarkMode` flag. Until the server
 * says yes the page is light whatever the stored preference, and the last
 * answer is cached so the pre-paint script can honour it without a flash.
 */

export const THEME_STORAGE_KEY = "healthos-theme";
export const DARK_MODE_ALLOWED_KEY = "healthos-dark-mode-allowed";
const DARK_QUERY = "(prefers-color-scheme: dark)";

export type ResolvedTheme = "light" | "dark";

function isPreference(v: unknown): v is ThemePreference {
  return v === "light" || v === "dark" || v === "system";
}

function readPref(): ThemePreference {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    if (isPreference(v)) return v;
  } catch {
    // localStorage unavailable; fall through to the default.
  }
  return "light";
}

function writePref(pref: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // localStorage unavailable; the choice lasts for this page only.
  }
}

function readAllowed(): boolean {
  try {
    return localStorage.getItem(DARK_MODE_ALLOWED_KEY) === "true";
  } catch {
    return false;
  }
}

function writeAllowed(allowed: boolean): void {
  try {
    if (allowed) localStorage.setItem(DARK_MODE_ALLOWED_KEY, "true");
    else localStorage.removeItem(DARK_MODE_ALLOWED_KEY);
  } catch {
    // localStorage unavailable; the answer lasts for this page only.
  }
}

function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.(DARK_QUERY).matches === true;
}

export function resolveTheme(pref: ThemePreference, prefersDark: boolean): ResolvedTheme {
  if (pref === "system") return prefersDark ? "dark" : "light";
  return pref;
}

let preference = readPref();
let darkAllowed = readAllowed();
let resolved: ResolvedTheme = darkAllowed ? resolveTheme(preference, systemPrefersDark()) : "light";
let watching = false;
const listeners = new Set<() => void>();

function apply(): void {
  resolved = darkAllowed ? resolveTheme(preference, systemPrefersDark()) : "light";
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (resolved === "dark") root.dataset.theme = "dark";
  else delete root.dataset.theme;
  // The browser chrome (address bar, overscroll, form controls) follows the
  // page background, which is the ambient surface token.
  const canvas = getComputedStyle(root).getPropertyValue("--color-surface-ambient").trim();
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", canvas || "");
  document.querySelector('meta[name="color-scheme"]')?.setAttribute("content", resolved);
  for (const l of listeners) l();
}

/** Idempotent boot: applies the stored choice and follows the OS while on `system`. */
export function initTheme(): void {
  if (watching || typeof window === "undefined") return;
  watching = true;
  window.matchMedia?.(DARK_QUERY).addEventListener?.("change", () => {
    if (preference === "system") apply();
  });
  apply();
}

export function setThemePreference(next: ThemePreference): void {
  if (next === preference) return;
  preference = next;
  writePref(next);
  apply();
}

/**
 * Record the `enableDarkMode` answer. Off forces light but keeps the stored
 * preference, so turning the flag back on restores the member's choice.
 */
export function setDarkModeAllowed(allowed: boolean): void {
  writeAllowed(allowed);
  if (allowed === darkAllowed) return;
  darkAllowed = allowed;
  apply();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme(): {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
} {
  const pref = useSyncExternalStore(
    subscribe,
    () => preference,
    () => "light" as const,
  );
  const res = useSyncExternalStore(
    subscribe,
    () => resolved,
    () => "light" as const,
  );
  return { preference: pref, resolved: res, setPreference: setThemePreference };
}
